"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import { SeriesModel, SeriesAccessModel } from "@api/models";
import { SeriesEpisodeList } from "./SeriesEpisodeList";
import { useSeriesReadEpisode } from "app/hooks";
import { EpisodeDisplayedOn } from "../../../types/types";
import { sessionStore } from "@lib/sessionStore";
import { getReadEpKey } from "app/constants";

type SeriesEpisodeListUserParams = {
  seriesHash: string;
  episodeCount: number;
  mode?: "seriesTop" | "epBottom";
  currentEpisodeId?: string;
  displayedOn: EpisodeDisplayedOn;
};

export function SeriesEpisodeListUser({
  seriesHash,
  episodeCount,
  mode,
  currentEpisodeId,
  displayedOn,
}: SeriesEpisodeListUserParams) {
  //mode
  if (!mode) {
    mode = "seriesTop";
  }

  //session
  const { sessionStatus } = useSyncExternalStore(
    sessionStore.subscribe,
    sessionStore.getSnapshot,
    sessionStore.getServerSnapshot,
  );

  //ロード中表示
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadEpStarted, setIsLoadEpStarted] = useState(false);

  //エピソード
  const [mySeriesWithFirstEps, setMySeriesWithFirstEps] = useState<SeriesModel>();
  const [mySeriesWithLastEps, setMySeriesWithLastEps] = useState<SeriesModel>();
  const [mySeriesAccessForFirstEps, setMySeriesAccessForFirstEps] = useState<SeriesAccessModel>();
  const [mySeriesAccessForLastEps, setMySeriesAccessForLastEps] = useState<SeriesAccessModel>();
  const [mySeriesStartIndex, setMySeriesStartIndex] = useState<number>(0);

  const { result: readEp, isLoading: isReadEpLoading } = useSeriesReadEpisode(
    seriesHash,
    sessionStatus === "authenticated",
  );

  useEffect(() => {
    // 既に処理開始済み=スキップ
    if (isLoadEpStarted) {
      return;
    }
    // ログイン状態がloading=スキップ
    if (sessionStatus === "loading") {
      return;
    }

    // viewer下は非ログインだったらスキップ
    if (mode === "epBottom" && sessionStatus === "unauthenticated") {
      return;
    }

    //seriesTopなら読了話から表示、でなければ1話から表示する
    let lastReadEpIdx = -1;
    if (mode === "seriesTop") {
      if (sessionStatus === "authenticated") {
        //APIロード中ならスキップ
        if (isReadEpLoading) {
          return;
        }
        //ログイン済みの場合は読了位置をAPIから取得
        const myLastReadEpIdxFromApi =
          readEp?.recentlyCompletedEpisode?.readingCompletedEpisodeIndex;
        if (myLastReadEpIdxFromApi !== undefined && myLastReadEpIdxFromApi !== null) {
          lastReadEpIdx = myLastReadEpIdxFromApi;
        }
      } else {
        //非ログインユーザは読了位置をlocalStorageから取得
        if (typeof window !== "undefined") {
          const readKey = getReadEpKey(seriesHash);
          const myLastReadEpIdxFromStorage = window.localStorage.getItem(readKey);
          if (myLastReadEpIdxFromStorage) {
            lastReadEpIdx = parseInt(myLastReadEpIdxFromStorage, 10);
          }
        }
        //未設定ならスキップして終了
        if (lastReadEpIdx === -1) {
          setIsLoadEpStarted(true);
          setIsLoading(false);
          return;
        }
      }
      if (lastReadEpIdx >= episodeCount - 1 || lastReadEpIdx < 1 || isNaN(lastReadEpIdx)) {
        lastReadEpIdx = 1;
      }
    } else {
      lastReadEpIdx = 1;
    }

    const firstEpCount = Math.min(episodeCount - lastReadEpIdx + 1, 5);

    let lastEpPromise, lastAccessPromise;
    const firstEpPromise = fetch(
      `/api/episodes?seriesHash=${seriesHash}&episodeTo=${lastReadEpIdx + firstEpCount - 1}&episodeFrom=${lastReadEpIdx}`, //memo:episodeFromは1から
    )
      .then((res) => res.json())
      .then((res) => {
        setMySeriesWithFirstEps((res.series as SeriesModel) || undefined);
      });
    const firstAccessPromise = fetch(
      `/api/series/access?seriesHash=${seriesHash}&episodeTo=${lastReadEpIdx + firstEpCount - 1}&episodeFrom=${lastReadEpIdx}`, //memo:episodeFromは1から
    )
      .then((res) => res.json())
      .then((res) => {
        setMySeriesAccessForFirstEps((res.seriesAccess as SeriesAccessModel) || undefined);
      });
    const lastEpCount = Math.min(episodeCount - (lastReadEpIdx + firstEpCount - 1), 5);
    if (lastEpCount > 0) {
      const lastEpFrom = episodeCount - lastEpCount + 1;
      lastEpPromise = fetch(
        `/api/episodes?seriesHash=${seriesHash}&episodeTo=${lastEpFrom + lastEpCount - 1}&episodeFrom=${lastEpFrom}`,
      )
        .then((res) => res.json())
        .then((res) => {
          setMySeriesStartIndex(lastReadEpIdx);
          setMySeriesWithLastEps((res.series as SeriesModel) || undefined);
        });
      lastAccessPromise = fetch(
        `/api/series/access?seriesHash=${seriesHash}&episodeTo=${lastEpFrom + lastEpCount - 1}&episodeFrom=${lastEpFrom}`,
      )
        .then((res) => res.json())
        .then((res) => {
          setMySeriesAccessForLastEps((res.seriesAccess as SeriesAccessModel) || undefined);
        });
    } else {
      setMySeriesStartIndex(lastReadEpIdx);
      setMySeriesWithLastEps(undefined);
      lastEpPromise = Promise.resolve();
      lastAccessPromise = Promise.resolve();
    }
    Promise.all([firstEpPromise, firstAccessPromise, lastEpPromise, lastAccessPromise]).finally(
      () => {
        setIsLoading(false);
      },
    );
    //一度ロードしたら二度呼ばない
    setIsLoadEpStarted(true);
  }, [sessionStatus, readEp, isReadEpLoading]);

  const mySeriesAccess: SeriesAccessModel = { seriesId: seriesHash, episodeAccesses: [] };
  if (mySeriesAccess.episodeAccesses) {
    if (mySeriesAccessForFirstEps && mySeriesAccessForFirstEps.episodeAccesses) {
      mySeriesAccess.episodeAccesses = mySeriesAccess.episodeAccesses.concat(
        mySeriesAccessForFirstEps.episodeAccesses,
      );
    }
    if (mySeriesAccessForLastEps && mySeriesAccessForLastEps.episodeAccesses) {
      mySeriesAccess.episodeAccesses = mySeriesAccess.episodeAccesses.concat(
        mySeriesAccessForLastEps.episodeAccesses,
      );
    }
  }

  return (
    <div
      className={
        "series-eplist mode-user" +
        (isLoading || sessionStatus === "loading" ? " mode-loading" : "") +
        (sessionStatus === "unauthenticated" ? " mode-no-user" : "") +
        (!isLoading && mySeriesWithFirstEps?.episodes.length ? " mode-has-eps" : "")
      }
    >
      {!isLoading && (
        <SeriesEpisodeList
          sort={mode === "epBottom" ? "new" : "old"}
          seriesWithFirstEps={mySeriesWithFirstEps}
          seriesWithLastEps={mySeriesWithLastEps}
          seriesAccess={mySeriesAccess}
          seriesStartIndex={mySeriesStartIndex}
          currentEpisodeId={currentEpisodeId}
          displayedOn={displayedOn}
        />
      )}
    </div>
  );
}
